<style>
    .total-row {
        border-top: 2px solid;
        font-weight: bold;
    }
</style>

<template>
    <b-container class="p-4" fluid>
        <h4>Option grant modeling tools</h4>
        <b-row class="pb-4">
            <b-col>
                <b-card-group deck>
                    <b-card title="Company parameters" title-tag="h5">
                        <b-form-group
                            label="Fair market value:"
                            label-cols="4"
                            label-align="right"
                            label-for="fmv"
                        >
                            <b-form-input
                                id="fmv"
                                v-model.lazy="params.fmv"
                                v-money="{
                                    decimal: '.',
                                    thousands: ',',
                                    prefix: '$',
                                    suffix: '/share',
                                }"
                            />
                            <small tabindex="-1" class="form-text text-muted">
                                The current fair market value per share
                                <b-badge
                                    v-b-tooltip.hover
                                    pill
                                    variant="info"
                                    title="This is the per share value for your company's Common stock. Depending on the size of your company, you should be able to ask your CEO, CFO, Controller, or General Counsel for this."
                                >?</b-badge>
                            </small>
                        </b-form-group>
                        <b-form-group
                            label="Preferred price:"
                            label-cols="4"
                            label-align="right"
                            label-for="preferred-price"
                        >
                            <b-form-input
                                id="preferred-price"
                                v-model.lazy="params.preferred_price"
                                v-money="{
                                    decimal: '.',
                                    thousands: ',',
                                    prefix: '$',
                                    suffix: '/share',
                                }"
                            />
                            <small tabindex="-1" class="form-text text-muted">
                                The last price per share for preferred stock
                                <b-badge
                                    v-b-tooltip.hover
                                    pill
                                    variant="info"
                                    title="This is the price your company's investors paid in the most recent round of funding. This is typically higher than the FMV, because investors receive preferences. Your company is not obligated to disclose this number to employees, but some do."
                                >?</b-badge>
                            </small>
                        </b-form-group>
                        <b-form-group
                            label="Total raised:"
                            label-cols="4"
                            label-align="right"
                            label-for="total-raised"
                        >
                            <b-form-input
                                id="total-raised"
                                v-model.lazy="params.total_raised"
                                v-money="{
                                    decimal: '.',
                                    thousands: ',',
                                    prefix: '$',
                                    suffix: '',
                                    precision: 0,
                                }"
                            />
                            <small tabindex="-1" class="form-text text-muted">
                                The total amount of money raised
                                <b-badge
                                    v-b-tooltip.hover
                                    pill
                                    variant="info"
                                    title="How much money has your company raised to date? You can usually find this number on Crunchbase. This number will act as the 'lower breakpoint' in your option payout calculation. That means if the company liquidates for any amount less than this number, the Common stock holders (employees) will not participate in the payout. Investors get their money back first because they took the biggest risk."
                                >?</b-badge>
                            </small>
                        </b-form-group>
                        <b-form-group
                            label="Post-money valuation:"
                            label-cols="4"
                            label-align="right"
                            label-for="valuation"
                        >
                            <b-form-input
                                id="valuation"
                                v-model.lazy="params.valuation"
                                v-money="{
                                    decimal: '.',
                                    thousands: ',',
                                    prefix: '$',
                                    suffix: '',
                                    precision: 0,
                                }"
                            />
                            <small tabindex="-1" class="form-text text-muted">
                                The company valuation after the last round of funding
                                <b-badge
                                    v-b-tooltip.hover
                                    pill
                                    variant="info"
                                    title="How much did investors value your company at during the last round of funding? You might be able to find this information in a press release from when your company raised money. This number will serve as the upper breakpoint in your option payout calculation. That means that if your company liquidates for any value greater than this, your option payout calculation is simple: ownership percentage * liquidation value. For liquidation values in-between the upper and lower breakpoints, 'liquidation preferences' come into play and can complicate the payout calculation. In this tool, we've added a '#LIQUPREFS!' error to any output values that fall between the breakpoints."
                                >?</b-badge>
                            </small>
                        </b-form-group>
                        <b-form-group
                            label="Potential exit value:"
                            label-cols="4"
                            label-align="right"
                            label-for="potential-exit"
                        >
                            <b-form-input
                                id="potential-exit"
                                v-model.lazy="params.potential_exit"
                                v-money="{
                                    decimal: '.',
                                    thousands: ',',
                                    prefix: '$',
                                    suffix: '',
                                    precision: 0,
                                }"
                            />
                            <small tabindex="-1" class="form-text text-muted">
                                The value your company could potentially exit at
                                <b-badge
                                    v-b-tooltip.hover
                                    pill
                                    variant="info"
                                    title="What do you think your company will be worth when it IPOs or gets acquired? This number is entirely theoretical and you should not make financial projections based on it, but playing with a range of exit values can at least give you a sense for what your option payout could potentially look like."
                                >?</b-badge>
                            </small>
                        </b-form-group>
                        <b-form-group
                            label="Fully diluted shares:"
                            label-cols="4"
                            label-align="right"
                            label-for="fully-diluted"
                        >
                            <money
                                id="fully-diluted"
                                class="form-control"
                                type="text"
                                readonly="readonly"
                                :value="fully_diluted"
                                v-bind="{
                                    decimal: '.',
                                    thousands: ',',
                                    prefix: '',
                                    suffix: '',
                                    precision: 0,
                                    masked: true,
                                }"
                            />
                            <small tabindex="-1" class="form-text text-muted">
                                Estimated fully diluted shares outstanding
                                <b-badge
                                    v-b-tooltip.hover
                                    pill
                                    variant="info"
                                    title="Post-money valuation divided by last preferred price"
                                >?</b-badge>
                            </small>
                        </b-form-group>
                    </b-card>
                    <b-card title="Option grants" title-tag="h5">
                        <b-table
                            small
                            responsive="sm"
                            :fields="[
                                {
                                    key: 'id',
                                    label: 'Grant ID',
                                },
                                'shares',
                                'strike',
                                {
                                    key: 'actions',
                                    label: '',
                                },
                            ]"
                            :items="grants"
                        >
                            <template #HEAD_actions>
                                <b-btn
                                    v-b-modal.input-modal
                                    v-b-tooltip.hover
                                    title="Add an option grant"
                                    size="sm"
                                    variant="outline-primary"
                                    pill
                                >
                                    +
                                </b-btn>
                            </template>
                            <template #actions="{ item }">
                                <b-btn
                                    v-b-tooltip.hover
                                    :title="`Remove grant ${item.id}`"
                                    size="sm"
                                    variant="outline-danger"
                                    pill
                                    @click="removeCol(item)"
                                >
                                    -
                                </b-btn>
                            </template>
                        </b-table>
                    </b-card>
                </b-card-group>
            </b-col>
        </b-row>
        <b-row>
            <b-col>
                <b-tabs content-class="mt-3">
                    <b-tab title="Notional value">
                        <b-table
                            outlined
                            small
                            striped
                            responsive="sm"
                            v-bind="notionalValueTable"
                        >
                            <template #taxable_gains="{ index, value }">
                                {{ value }}
                                <b-badge
                                    v-if="index === grants.length"
                                    v-b-tooltip.hover
                                    pill
                                    variant="info"
                                    title="This is the taxable amount of income you would recognize if you exercised all your options. The IRS sees this as real income (even though your shares are still illiquid until your company IPOs or gets bought). The larger this gain is, the higher your chances of triggering AMT (Alternative Minimum Tax)."
                                >?</b-badge>
                            </template>
                            <template #notional_value="{ index, value }">
                                {{ value }}
                                <b-badge
                                    v-if="index === grants.length"
                                    v-b-tooltip.hover
                                    pill
                                    variant="info"
                                    title="Notional value is the estimated dollar amount of your options today. It does not factor in the cost of exercising your options. Note: this value is based off your last preferred price. Depending on your company, this could be less than or greater than the actual value of your options. All options are technically worth $0 until a liquidity event."
                                >?</b-badge>
                            </template>
                            <template #current_value="{ index, value }">
                                {{ value }}
                                <b-badge
                                    v-if="index === grants.length"
                                    v-b-tooltip.hover
                                    pill
                                    variant="info"
                                    title="This is the estimated current dollar value of your options, after subtracting the cost to exercise. Theoretically, this is an estimate of what you might come away with if your company was to liquidate at its current value. Again, this is only an estimate based on your last preferred price. Depending on your company, this could be less than or greater than the current theoretical value of your options."
                                >?</b-badge>
                            </template>
                        </b-table>
                    </b-tab>
                    <b-tab title="Option payout">
                        <b-table
                            outlined
                            small
                            striped
                            responsive="sm"
                            v-bind="optionPayoutTable"
                        >
                            <template #ownership_percentage="{ index, value }">
                                {{ value }}
                                <b-badge
                                    v-if="index === grants.length"
                                    v-b-tooltip.hover
                                    pill
                                    variant="info"
                                    title="This is your estimated ownership percentage of the company as a whole."
                                >?</b-badge>
                            </template>
                            <template #option_payout="{ index, value }">
                                {{ value }}
                                <b-badge
                                    v-if="index === grants.length"
                                    v-b-tooltip.hover
                                    pill
                                    variant="info"
                                    title="This is your option payout based on your estimated ownership percentage and the theoretical exit value you entered."
                                >?</b-badge>
                            </template>
                            <template #net_option_payout="{ index, value }">
                                {{ value }}
                                <b-badge
                                    v-if="index === grants.length"
                                    v-b-tooltip.hover
                                    pill
                                    variant="info"
                                    title="This is your option payout based on your estimated ownership percentage and the theoretical exit value you entered, after subtracting out the cost to exercise your options."
                                >?</b-badge>
                            </template>
                        </b-table>
                    </b-tab>
                </b-tabs>
                <b-modal
                    ref="modal"
                    id="input-modal"
                    size="md"
                    title="Add an option grant"
                    @ok="addColumn"
                    @cancel="add_grant = {}"
                    @shown="() => $refs.modalInput.focus()"
                >
                    <b-form-group
                        label="Grant ID:"
                        label-cols-sm="3"
                        label-align-sm="right"
                        label-for="grant-id"
                    >
                        <b-form-input
                            ref="modalInput"
                            id="grant-id"
                            v-model.lazy="add_grant.id"
                        />
                    </b-form-group>
                    <b-form-group
                        label="Shares:"
                        label-cols-sm="3"
                        label-align-sm="right"
                        label-for="shares"
                    >
                        <b-form-input
                            id="shares"
                            v-model.lazy="add_grant.shares"
                            v-money="{
                                decimal: '.',
                                thousands: ',',
                                prefix: '',
                                suffix: ' shares',
                                precision: 0,
                            }"
                        />
                    </b-form-group>
                    <b-form-group
                        label="Strike:"
                        label-cols-sm="3"
                        label-align-sm="right"
                        label-for="strike"
                    >
                        <b-form-input
                            id="strike"
                            @keyup.enter="() => { $refs.modal.hide(); addColumn(); }"
                            v-model.lazy="add_grant.strike"
                            v-money="{
                                decimal: '.',
                                thousands: ',',
                                prefix: '$',
                                suffix: '/share',
                                precision: 2,
                            }"
                        />
                    </b-form-group>
                </b-modal>
            </b-col>
        </b-row>
    </b-container>
</template>

<script>
    // localStorage persistence
    const STORAGE_KEY = 'option-grant-modeling';
    const defaultParams = {
        fmv: '',
        preferred_price: '',
        valuation: '',
        total_raised: '',
        potential_exit: '',
    };
    const storage = {
        fetch(key) {
            const items = JSON.parse(localStorage.getItem(`${STORAGE_KEY}-${key}`) || '{}');
            switch (key) {
                case 'params':
                    return { ...defaultParams, ...items };
                case 'grants':
                    return Array.isArray(items) ? items : [];
                default:
                    return items;
            }
        },
        save(key, items) {
            localStorage.setItem(`${STORAGE_KEY}-${key}`, JSON.stringify(items));
        }
    };
    export default {
        name: 'app',
        data() {
            return {
                params: storage.fetch('params'),
                grants: storage.fetch('grants'),
                add_grant: {},
            };
        },
        watch: {
            params: {
                deep: true,
                handler(params) {
                    storage.save('params', params);
                },
            },
            grants: {
                deep: true,
                handler(grants) {
                    storage.save('grants', grants.filter(x => x));
                },
            },
        },
        computed: {
            notionalValueTable() {
                const formatter = (value) => {
                    if (value === '') {
                        return '';
                    }
                    return `$${String((value || 0).toFixed(2)).replace(/\B(?=(?:\d{3})+(?!\d))/g,',')}`;
                };
                const rows = [];
                const fmv = Number.parseFloat(this.params.fmv.replace(/[^0-9.]/g, ''));
                const price = Number.parseFloat(this.params.preferred_price.replace(/[^0-9.]/g, ''));
                let totalShares = 0;
                let totalCost = 0.0;
                let totalGains = 0.0;
                let totalNotional = 0.0;
                let totalValue = 0.0;
                this.grants.forEach((grant) => {
                    if (!grant) {
                        return;
                    }
                    const shares = Number.parseInt(grant.shares.replace(/[^0-9]/g, ''));
                    const strike = Number.parseFloat(grant.strike.replace(/[^0-9.]/g, ''));
                    const cost = shares * strike;
                    const spread = Math.max(fmv - strike, 0);
                    const gains = spread * shares;
                    const notional = price * shares;
                    const value = notional - cost;
                    totalShares += shares;
                    totalCost += cost;
                    totalGains += gains;
                    totalNotional += notional;
                    totalValue += value;
                    rows.push({
                        grant_id: grant.id,
                        shares,
                        strike,
                        cost_to_exercise: cost,
                        strike_spread: spread,
                        taxable_gains: gains,
                        notional_value: notional,
                        current_value: value,
                    });
                });
                rows.push({
                    grant_id: 'Total',
                    shares: totalShares,
                    strike: '',
                    cost_to_exercise: totalCost,
                    strike_spread: '',
                    taxable_gains: totalGains,
                    notional_value: totalNotional,
                    current_value: totalValue,
                    total: true,
                });
                return {
                    fields: [
                        {
                            key: 'grant_id',
                            label: 'Grant ID',
                        },
                        {
                            key: 'shares',
                            formatter(value) {
                                return String(value || 0).replace(/\B(?=(?:\d{3})+(?!\d))/g,',');
                            },
                        },
                        {
                            key: 'strike',
                            formatter,
                        },
                        {
                            key: 'cost_to_exercise',
                            label: 'Cost to exercise',
                            formatter,
                        },
                        {
                            key: 'strike_spread',
                            label: 'Strike spread',
                            formatter,
                        },
                        {
                            key: 'taxable_gains',
                            label: 'Taxable gains',
                            formatter,
                        },
                        {
                            key: 'notional_value',
                            label: 'Notional value',
                            formatter,
                        },
                        {
                            key: 'current_value',
                            label: 'Current value',
                            formatter,
                        },
                    ],
                    items: rows,
                    'tbody-tr-class': this.rowClass,
                };
            },
            optionPayoutTable() {
                const formatter = (value) => {
                    if (value === '') {
                        return '';
                    }
                    if (Number.isNaN(value)) {
                        return 'LiquPref';
                    }
                    return `$${String((value || 0).toFixed(2)).replace(/\B(?=(?:\d{3})+(?!\d))/g,',')}`;
                };
                const rows = [];
                const exit = Number.parseInt(this.params.potential_exit.replace(/[^0-9.]/g, ''));
                const raised = Number.parseInt(this.params.total_raised.replace(/[^0-9.]/g, ''));
                const valuation = Number.parseInt(this.params.valuation.replace(/[^0-9.]/g, ''));
                let totalShares = 0;
                let totalCost = 0.0;
                let totalOwnership = 0.0;
                let totalPayout = 0.0;
                let totalNetPayout = 0.0;
                this.grants.forEach((grant) => {
                    if (!grant) {
                        return;
                    }
                    const shares = Number.parseInt(grant.shares.replace(/[^0-9]/g, ''));
                    const strike = Number.parseFloat(grant.strike.replace(/[^0-9.]/g, ''));
                    const cost = shares * strike;
                    const ownership = shares / this.fully_diluted;
                    const payout = exit <= raised ? 0 : exit <= valuation ? NaN : ownership * exit;
                    const netPayout = payout - cost;
                    totalShares += shares;
                    totalCost += cost;
                    totalOwnership += ownership;
                    totalPayout += payout;
                    totalNetPayout += netPayout;
                    rows.push({
                        grant_id: grant.id,
                        shares,
                        strike,
                        cost_to_exercise: cost,
                        ownership_percentage: ownership,
                        option_payout: payout,
                        net_option_payout: netPayout,
                    });
                });
                rows.push({
                    grant_id: 'Total',
                    shares: totalShares,
                    strike: '',
                    cost_to_exercise: totalCost,
                    ownership_percentage: totalOwnership,
                    option_payout: totalPayout,
                    net_option_payout: totalNetPayout,
                    total: true,
                });
                return {
                    fields: [
                        {
                            key: 'grant_id',
                            label: 'Grant ID',
                        },
                        {
                            key: 'shares',
                            formatter(value) {
                                return String(value || 0).replace(/\B(?=(?:\d{3})+(?!\d))/g,',');
                            },
                        },
                        {
                            key: 'strike',
                            formatter,
                        },
                        {
                            key: 'cost_to_exercise',
                            label: 'Cost to exercise',
                            formatter,
                        },
                        {
                            key: 'ownership_percentage',
                            label: 'Ownership percentage',
                            formatter(value) {
                                if (value === '') {
                                    return '';
                                }
                                return `${((value || 0) * 100).toPrecision(3)}%`;
                            },
                        },
                        {
                            key: 'option_payout',
                            label: 'Option payout',
                            formatter,
                        },
                        {
                            key: 'net_option_payout',
                            label: 'Net option payout',
                            formatter,
                        },
                    ],
                    items: rows,
                    'tbody-tr-class': this.rowClass,
                };
            },
            fully_diluted() {
                const valuation = Number.parseInt(this.params.valuation.replace(/[^0-9.]/g, ''));
                const preferred = Number.parseFloat(this.params.preferred_price.replace(/[^0-9.]/g, ''));
                return Math.ceil(valuation / preferred);
            },
        },
        methods: {
            addColumn() {
                const { id, shares, strike } = this.add_grant;
                const grants = [ ...this.grants ];
                this.add_grant = {};
                if (grants.indexOf(id) >= 0) {
                    return;
                }
                grants.push({ id, shares, strike });
                grants.sort((a, b) => {
                    const x = a.id;
                    const y = b.id;
                    if (x > y) {
                        return 1;
                    } else if (x < y) {
                        return -1;
                    }
                    return 0;
                });
                this.grants = grants.filter(x => x);
            },
            removeCol(grant) {
                const grants = [ ...this.grants ];
                grants[grants.indexOf(grant)] = null;
                this.grants = grants.filter(x => x);
            },
            rowClass(item) {
                if (!item || !item.total) {
                    return;
                }
                return 'total-row';
            },
        },
    };
</script>
